import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Input, InputNumber, Modal, Select, Table, Tag, message } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import styled from "styled-components";
import { useSelector } from "react-redux";
import UseApi from "../../Reusable/CallApi/UseApi";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  .header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .title {
    font-size: 20px;
    font-weight: 500;
    color: #1f243b;
  }

  .search-input {
    width: 220px;
    border-radius: 20px;
  }
`;

const STATUS_COLOR = {
  pending: "gold",
  passed: "green",
  failed: "red",
};

const formatDate = (value) => {
  const parsed = value ? dayjs(value) : null;
  return parsed && parsed.isValid() ? parsed.format("Do MMM YYYY") : "-";
};

// A Tutor records a score directly against a person's Test/Assignment
// unit here - there's no separate "learner submits for review" step
// anywhere in this app (no learner login/portal exists yet), so this is
// a scoring ENTRY workflow, not a review-a-submission one. See
// db/schema/create_scoring_table.sql for the reasoning behind the data
// model this is built on.
const Scoring = () => {
  const clientId = useSelector((state) => state.authTracking.userInfo.clientId);
  const { data, loading, request } = UseApi();
  const { request: unitsRequest } = UseApi();
  const { request: peopleRequest } = UseApi();
  const { request: createRequest, loading: creating } = UseApi();

  const [searchTerm, setSearchTerm] = useState("");
  const [scorableUnits, setScorableUnits] = useState([]);
  const [people, setPeople] = useState([]);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  const fetchScores = () => {
    if (!clientId) return;
    // UseApi's request() rethrows on failure - uncaught here, this was a
    // genuine unhandled promise rejection (visible as CRA's dev error
    // overlay whenever this endpoint fails, e.g. before it's deployed).
    // The loading/error state this hook already exposes is all the UI
    // needs; nothing here needs the resolved value itself.
    request({ endPoint: `/scoring/getscores/${clientId}`, method: "get" }).catch(() => {});
  };

  useEffect(() => {
    fetchScores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const scores = data?.response || [];

  const filteredScores = useMemo(() => {
    const value = searchTerm.toLowerCase();
    return scores.filter(
      (item) =>
        (item.unit_title || "").toLowerCase().includes(value) ||
        (item.employee_name || "").toLowerCase().includes(value) ||
        (item.course_name || "").toLowerCase().includes(value)
    );
  }, [scores, searchTerm]);

  const columns = [
    { title: "Assessment", dataIndex: "unit_title", key: "unit_title" },
    { title: "Employee", dataIndex: "employee_name", key: "employee_name" },
    { title: "Course", dataIndex: "course_name", key: "course_name" },
    {
      title: "Scored on",
      dataIndex: "scored_on",
      key: "scored_on",
      render: formatDate,
    },
    {
      title: "Score",
      dataIndex: "score",
      key: "score",
      render: (value) => (value ?? "-"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value) => (
        <Tag color={STATUS_COLOR[value] || "default"}>
          {value ? value.charAt(0).toUpperCase() + value.slice(1) : "-"}
        </Tag>
      ),
    },
  ];

  const openCreateModal = async () => {
    form.resetFields();
    setOpen(true);
    const [unitsResult, peopleResult] = await Promise.all([
      unitsRequest({ endPoint: `/scoring/getscorableunits/${clientId}`, method: "get" }).catch(() => null),
      peopleRequest({ endPoint: `/people/getuser/${clientId}`, method: "get" }).catch(() => null),
    ]);
    setScorableUnits(unitsResult?.response || []);
    setPeople(peopleResult?.response || []);
  };

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      // course_unit is the JSON.stringify'd { course_id, unit_id } used as
      // the Select's option value (Select values must be primitives, not
      // objects) - parsed back out here instead of in onChange, so the
      // Select's own value always stays a string matching its options.
      const { course_id, unit_id } = JSON.parse(values.course_unit);
      const unit = scorableUnits.find(
        (u) => u.course_id === course_id && u.unit_id === unit_id
      );
      await createRequest({
        endPoint: `/scoring/createscore/${clientId}`,
        method: "post",
        data: {
          course_id,
          unit_id,
          unit_title: unit?.unit_title,
          people_id: values.people_id,
          score: values.score,
          status: values.status,
        },
      });
      message.success("Score recorded successfully");
      setOpen(false);
      fetchScores();
    } catch (error) {
      // A failed form.validateFields() lands here too - antd already
      // renders those inline under each field, so only surface a toast
      // for an actual API failure (errorFields is antd's own shape for
      // a validation rejection).
      if (!error?.errorFields) {
        message.error("Failed to record score");
      }
    }
  };

  return (
    <Wrapper>
      <div className="header-row">
        <div className="title">Scoring</div>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
          Add Score
        </Button>
      </div>

      <Input
        className="search-input"
        placeholder="Search"
        prefix={<SearchOutlined />}
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
      />

      <Table
        rowKey={(record, index) => record.assessment_id ?? index}
        columns={columns}
        dataSource={filteredScores}
        loading={loading}
        pagination={false}
        locale={{
          emptyText:
            scorableUnits.length === 0 && scores.length === 0
              ? "No scores recorded yet"
              : "No matching scores",
        }}
      />

      <Modal
        title="Add Score"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={handleCreate}
        confirmLoading={creating}
        okText="Save"
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="course_unit"
            label="Assessment"
            rules={[{ required: true, message: "Pick a Test or Assignment unit" }]}
          >
            <Select
              placeholder={
                scorableUnits.length === 0
                  ? "No Test/Assignment units found in any course"
                  : "Select a Test or Assignment"
              }
              optionFilterProp="label"
              options={scorableUnits.map((unit) => ({
                value: JSON.stringify({ course_id: unit.course_id, unit_id: unit.unit_id }),
                label: `${unit.course_name} - ${unit.unit_title} (${unit.unit_type})`,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="people_id"
            label="Employee"
            rules={[{ required: true, message: "Pick an employee" }]}
          >
            <Select
              placeholder="Select employee"
              optionFilterProp="label"
              options={people.map((person) => ({
                value: person.people_id,
                label: `${person.first_name || ""} ${person.last_name || ""}`.trim(),
              }))}
            />
          </Form.Item>
          <Form.Item name="score" label="Score">
            <InputNumber style={{ width: "100%" }} min={0} max={100} />
          </Form.Item>
          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: "Pick a status" }]}
            initialValue="pending"
          >
            <Select
              options={[
                { label: "Pending", value: "pending" },
                { label: "Passed", value: "passed" },
                { label: "Failed", value: "failed" },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Wrapper>
  );
};

export default Scoring;
